import React, { useState, useRef, useEffect, useCallback } from "react";
import { Alert, Badge, Button, Form, Spinner } from "react-bootstrap";
import axios from "../utils/api";
import "../styles/StoreChat.css";

const API_URL = process.env.REACT_APP_API_URL_BASE || "http://localhost:6020/";

const SUGGESTED_QUESTIONS = [
  "What items are low on stock?",
  "What are my top 10 best selling items?",
  "Which vendor generates the most revenue?",
  "What's the total revenue so far?",
  "How many bottles of [product] do I have left?",
  "Show me a summary of all vendors",
  "What items are completely out of stock?",
  "Which items haven't sold at all?",
];

function MessageBubble({ message }) {
  const isUser = message.role === "user";
  return (
    <div className={`chat-message ${isUser ? "chat-message--user" : "chat-message--assistant"}`}>
      <div className={`chat-bubble ${isUser ? "chat-bubble--user" : "chat-bubble--assistant"}`}>
        {message.content.split("\n").map((line, i) => (
          <span key={i}>
            {line}
            {i < message.content.split("\n").length - 1 && <br />}
          </span>
        ))}
      </div>
      <div className="chat-meta">
        {isUser ? "You" : "🤖 Assistant"}{" "}
        {message.timestamp && (
          <span className="text-muted">
            · {new Date(message.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Slide-out chat drawer.
 * Props:
 *   open     – boolean, whether drawer is visible
 *   onClose  – callback to close the drawer
 */
function StoreChat({ open, onClose }) {
  const [messages, setMessages]     = useState([]);
  const [input, setInput]           = useState("");
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const bottomRef                   = useRef(null);
  const inputRef                    = useRef(null);
  const drawerRef                   = useRef(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Focus input when drawer opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  // Close on Escape
  const handleKeyDownGlobal = useCallback((e) => {
    if (e.key === "Escape" && open) onClose?.();
  }, [open, onClose]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDownGlobal);
    return () => document.removeEventListener("keydown", handleKeyDownGlobal);
  }, [handleKeyDownGlobal]);

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const sendMessage = async (text) => {
    const userText = (text || input).trim();
    if (!userText || loading) return;

    const userMsg = { role: "user", content: userText, timestamp: Date.now() };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      const { data } = await axios.post(`${API_URL}api/chat`, {
        messages: history.map(({ role, content }) => ({ role, content })),
      });
      if (!data.success) throw new Error(data.message || "Chat failed");
      setMessages([
        ...history,
        { role: "assistant", content: data.reply, timestamp: Date.now() },
      ]);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Something went wrong";
      setError(msg);
      setMessages(messages);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError(null);
    inputRef.current?.focus();
  };

  const isEmpty = messages.length === 0;

  return (
    <>
      {/* ── Backdrop overlay ── */}
      <div
        className={`chat-drawer-overlay ${open ? "chat-drawer-overlay--visible" : ""}`}
        onClick={onClose}
      />

      {/* ── Drawer panel ── */}
      <div
        ref={drawerRef}
        className={`chat-drawer ${open ? "chat-drawer--open" : ""}`}
      >
        {/* Header */}
        <div className="chat-drawer__header">
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: "1.3rem" }}>🤖</span>
            <div>
              <div className="fw-bold" style={{ lineHeight: 1.2 }}>Store Assistant</div>
              <div className="text-muted" style={{ fontSize: "0.72rem" }}>Ask about inventory, sales & vendors</div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {messages.length > 0 && (
              <button className="chat-drawer__icon-btn" onClick={clearChat} title="Clear chat">
                🗑️
              </button>
            )}
            <button className="chat-drawer__icon-btn chat-drawer__close-btn" onClick={onClose} title="Close">
              ✕
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="mx-3 mt-2 mb-0" style={{ fontSize: "0.82rem" }}>
            {error}
          </Alert>
        )}

        {/* ── Chat body ── */}
        <div className="chat-drawer__body">
          {isEmpty ? (
            <div className="chat-empty">
              <div className="chat-empty__icon">🥃</div>
              <h6>What would you like to know?</h6>
              <p className="text-muted small">Ask anything about your store.</p>
              <div className="chat-suggestions">
                {SUGGESTED_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    className="chat-suggestion-chip"
                    onClick={() => sendMessage(q)}
                    disabled={loading}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="chat-messages">
              {messages.map((msg, idx) => (
                <MessageBubble key={idx} message={msg} />
              ))}
              {loading && (
                <div className="chat-message chat-message--assistant">
                  <div className="chat-bubble chat-bubble--assistant chat-bubble--typing">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        {/* ── Input bar ── */}
        <div className="chat-drawer__footer">
          {!isEmpty && (
            <div className="chat-suggestions chat-suggestions--inline mb-2">
              {SUGGESTED_QUESTIONS.slice(0, 3).map((q) => (
                <button
                  key={q}
                  className="chat-suggestion-chip chat-suggestion-chip--sm"
                  onClick={() => sendMessage(q)}
                  disabled={loading}
                >
                  {q}
                </button>
              ))}
            </div>
          )}
          <div className="d-flex gap-2">
            <Form.Control
              ref={inputRef}
              as="textarea"
              rows={1}
              placeholder="Ask about your store…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              className="chat-input"
            />
            <Button
              variant="primary"
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className="chat-send-btn"
            >
              {loading ? <Spinner animation="border" size="sm" /> : "➤"}
            </Button>
          </div>
          <div className="text-muted mt-1" style={{ fontSize: "0.7rem" }}>
            <Badge bg="secondary" style={{ fontSize: "0.65rem" }}>Enter</Badge> send
            &nbsp;·&nbsp;
            <Badge bg="secondary" style={{ fontSize: "0.65rem" }}>Esc</Badge> close
          </div>
        </div>
      </div>
    </>
  );
}

export default StoreChat;
